import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider } from 'antd';
import { FormattedMessage } from 'react-intl';
import Pficon from 'components/Pficon';

import messages from 'containers/TemplatePreviewPage/TemplateFlows/ComplexFlow/messages';
import {
  getDefaultVariableValue,
  hasFieldError,
} from 'containers/TemplatePreviewPage/utils';
import classNames from 'classnames';

import { FIELD_TYPE } from 'containers/TemplatePreviewPage/constants';
import SimpleText from '../SimpleText';
import TextWithMedia from '../TextWithMedia';

const ConceptRepeater = ({
  question,
  questionIndex,
  onUpdateVariable,
  skipTopMargin,
}) => {
  const {
    value: values = [],
    hasError,
    isPristine,
    body,
    placeholder,
    contentBody,
    contentPlaceholder,
    min: minAnswers = 0,
    max: maxAnswers = 100,
    supportedMediaTypes,
  } = question;

  const [initialQuestionSize, setInitialQuestionSize] = useState();

  useEffect(() => {
    if (!initialQuestionSize && values.length > 0) {
      setInitialQuestionSize(values.length);
    }
  }, [values, initialQuestionSize]);

  const underMinCount = Math.max(minAnswers - values.length, 0);
  const overMaxCount = Math.max(values.length - maxAnswers, 0);

  const missingMessage =
    underMinCount === 1
      ? messages.validation.fieldIncomplete
      : messages.validation.fieldIncompletePlural;

  const extraValuesMessage =
    overMaxCount === 1
      ? messages.validation.fieldHasExtra
      : messages.validation.fieldHasExtraPlural;

  const onUpdate = useCallback(
    (partialValue, updateIndex) => {
      const updatedValues = values.map((existingValue, index) => {
        if (index === updateIndex) {
          return Object.assign({}, existingValue, partialValue);
        }
        return existingValue;
      });
      onUpdateVariable(updatedValues);
    },
    [onUpdateVariable, values],
  );

  const onUpdateSingleTextValue = useCallback(
    (index) => (value) => {
      onUpdate({ name: value }, index);
    },
    [onUpdate],
  );
  const onUpdateSingleTextWithMediaValue = useCallback(
    (index) => (value) => {
      onUpdate(value, index);
    },
    [onUpdate],
  );

  const onAddValue = useCallback(() => {
    onUpdateVariable([
      ...values,
      getDefaultVariableValue({ fieldType: FIELD_TYPE.CONCEPT_REPEATER }),
    ]);
  }, [onUpdateVariable, values]);

  const onRemoveValue = useCallback(
    (indexToRemove) => {
      onUpdateVariable(values.filter((_, index) => index !== indexToRemove));
    },
    [onUpdateVariable, values],
  );
  const addDisabled = values.length >= maxAnswers;

  return (
    <ul className="flex flex-col margin-none padding-none">
      {values.map((value = {}, index) => {
        const subTextQuestion = {
          body,
          fieldType: FIELD_TYPE.TEXT,
          value: value.name,
          placeholder,
          required: true,
          isPristine,
        };
        const subTextWithMediaTextQuestion = {
          fieldType: FIELD_TYPE.TEXT_WITH_MEDIA,
          value: { text: value.text, media: value.media },
          body: contentBody,
          placeholder: contentPlaceholder,
          required: true,
          isPristine,
          supportedMediaTypes,
        };
        const subTextQuestionHasError = hasFieldError(subTextQuestion);
        const subTextWithMediaQuestionHasError = hasFieldError(
          subTextWithMediaTextQuestion,
        );
        const deleteDisabled = values.length <= 1;
        Object.assign(subTextQuestion, { hasError: subTextQuestionHasError });
        Object.assign(subTextWithMediaTextQuestion, {
          hasError: subTextWithMediaQuestionHasError,
        });
        return (
          <li className="flex">
            <div className="flex-1">
              <SimpleText
                skipTopMargin={index === 0 && skipTopMargin}
                question={subTextQuestion}
                shouldAutoFocus={
                  (questionIndex === 0 && index === 0) ||
                  (initialQuestionSize && values.length > initialQuestionSize)
                }
                onUpdateVariable={onUpdateSingleTextValue(index)}
                hideErrors
              />
              <TextWithMedia
                question={subTextWithMediaTextQuestion}
                autoFocus={false}
                onUpdateVariable={onUpdateSingleTextWithMediaValue(index)}
                hideErrors
              />
            </div>
            <Divider
              type="vertical"
              className={classNames(
                'horizontal-margin-xs',
                'background-grey-5',
                'height-none',
                {
                  'margin-top-lg margin-btm-none': !(
                    index === 0 && skipTopMargin
                  ),
                  'margin-vertical-none': index === 0 && skipTopMargin,
                },
              )}
            />
            <Button
              disabled={deleteDisabled}
              type="button"
              onClick={() => onRemoveValue(index)}
              style={{
                height: 38,
                lineHeight: '38px',
                marginTop: index === 0 && skipTopMargin ? 30 : 54,
              }}
              className={classNames(
                'outline-blue',
                'margin-top-xxs',
                'color-grey-10',
                'text-align-center',
                'padding-vertical-none',
                'padding-horizontal-xs',
                'border-none',
                'background-transparent',
                'shadow-none',
                'flex flex-align-items--center',
                {
                  'color-grey-6': deleteDisabled,
                  'color-grey-10': !deleteDisabled,
                },
              )}
            >
              <Pficon type="close" className="text-size-h3" />
            </Button>
          </li>
        );
      })}
      {!isPristine && hasError && Boolean(underMinCount) && (
        <li className="display-block margin-btm-sm">
          <span className="color-red-6" key={`${question.id}-validation`}>
            <FormattedMessage
              {...missingMessage}
              values={{
                missingValuesCount: underMinCount,
              }}
            />
          </span>
        </li>
      )}
      {!isPristine && hasError && Boolean(overMaxCount) && (
        <li className="display-block margin-btm-sm">
          <span className="color-red-6" key={`${question.id}-validation-count`}>
            <FormattedMessage
              {...extraValuesMessage}
              values={{
                extraValuesCount: overMaxCount,
              }}
            />
          </span>
        </li>
      )}
      {!isPristine && hasError && !(underMinCount || overMaxCount) && (
        <li className="display-block margin-vertical-sm">
          <span className="color-red-6">
            <FormattedMessage {...messages.validation.fieldHasSubError} />
          </span>
        </li>
      )}
      <li className="display-block margin-top-sm">
        <Button
          type="button"
          onClick={onAddValue}
          disabled={addDisabled}
          className={classNames(
            'outline-blue',
            'text-size-secondary',
            'color-grey-1',
            'border-none',
            'padding-horizontal-xs',
            'padding-vertical-xxs',
            'shadow-none',
            {
              'background-grey-8': addDisabled,
              'background-grey-8-hover': addDisabled,
              'background-grey-10': !addDisabled,
            },
          )}
          style={{ height: 'auto' }}
        >
          Add another
        </Button>
      </li>
    </ul>
  );
};

ConceptRepeater.propTypes = {
  question: PropTypes.object,
  questionGroupIndex: PropTypes.number,
  questionIndex: PropTypes.number,
  updateVariableValues: PropTypes.func.isRequired,
};

export default ConceptRepeater;
